import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { X, Send, Loader2, Mail, AlertCircle } from "lucide-react";
import { useOutreach } from "./outreach/useOutreach";
import { OutreachEmailForm } from "./outreach/OutreachEmailForm";
import { OutreachLinkedin } from "./outreach/OutreachLinkedin";

const GenerateStep = ({ onGenerate }) => (
  <div className="text-center py-8">
    <div className="p-4 rounded-2xl bg-ism-teal/10 border border-ism-teal/20 inline-block mb-4"><Send className="h-10 w-10 text-ism-teal" /></div>
    <p className="text-gray-300 mb-2">Generate a personalized email and LinkedIn message</p>
    <p className="text-xs text-gray-500 mb-6">AI will craft outreach based on their profile and your products</p>
    <Button onClick={onGenerate} className="bg-ism-teal hover:bg-ism-teal/90 text-black font-medium px-6" data-testid="generate-outreach-btn"><Send className="h-4 w-4 mr-2" /> Generate Outreach</Button>
  </div>
);

const Generating = () => (
  <div className="text-center py-12" data-testid="outreach-generating">
    <Loader2 className="h-8 w-8 animate-spin text-ism-teal mx-auto mb-4" />
    <p className="text-gray-400">Crafting personalized outreach...</p>
    <p className="text-xs text-gray-500 mt-1">Generating email + LinkedIn message</p>
  </div>
);

const OutreachModal = ({ isOpen, onClose, lead, userId }) => {
  const o = useOutreach({ lead, userId });
  if (!isOpen) return null;
  const { contactName, contactTitle, companyName, linkedinUrl } = o.contact;

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-[60] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <motion.div className="glass-card relative z-10 w-full max-w-xl max-h-[90vh] flex flex-col rounded-2xl overflow-hidden border border-ism-teal/30"
          initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }}
          onClick={(e) => e.stopPropagation()} data-testid="outreach-modal">
          <div className="p-5 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-ism-teal/10 to-transparent flex-shrink-0">
            <div>
              <h2 className="text-lg text-white font-medium flex items-center gap-2"><Mail className="h-5 w-5 text-ism-teal" /> Outreach to {contactName}</h2>
              <p className="text-xs text-gray-400 mt-0.5">{contactTitle}{contactTitle && companyName && " at "}{companyName}</p>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors" data-testid="outreach-modal-close"><X className="h-5 w-5" /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {!o.outreach && !o.generating && <GenerateStep onGenerate={o.handleGenerate} />}
            {o.generating && <Generating />}
            {o.outreach && !o.generating && (
              <>
                <OutreachEmailForm email={o.email} lead={lead} sending={o.sending} sent={o.sent} onSend={o.handleSend} />
                <div className="border-t border-white/10" />
                <OutreachLinkedin message={o.outreach?.linkedin?.followUpMessage} linkedinUrl={linkedinUrl} copied={o.copiedLinkedin} onCopy={o.handleCopyLinkedin} />
              </>
            )}
            {o.error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30" data-testid="outreach-error">
                <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" /><p className="text-sm text-red-400">{o.error}</p>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-white/10 bg-white/5 flex-shrink-0">
            <Button onClick={onClose} variant="ghost" className="w-full text-gray-400 hover:text-white" data-testid="outreach-modal-cancel">{o.sent ? "Done" : "Cancel"}</Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OutreachModal;
